import React, { Fragment, Suspense, useCallback, useRef } from 'react';
import { Hud, Text } from '@react-three/drei';
import { ThreeElements } from '@react-three/fiber';
import { ErrorBoundary } from 'react-error-boundary';
import { BufferGeometry, Color, ExtrudeGeometry, LineSegments, Matrix4, Shape, Vector2, Vector3 } from 'three';

import { PlateAnalysis, PlatePair } from '@paragon/contract-library';
import { angleEquals, convertDegreesToRadians, Point2D, Polygon2D, straightAngle } from '@paragon/geometry-library';
import {
  getPlatePairPlate1Matrix,
  getPlatePairPlate2Matrix,
  getPlateThicknessForVisibility,
  mth18HingeHoleRadius,
  plateSlotLength,
  RenderOrder,
  smh18HingeHoleRadius,
} from '@paragon/three-shared';

import { getColorFromCapacityRatio } from '../../capacityColors';
import { useAppDispatch, useAppSelector } from '../../components/hooks';
import { getSelectedItemIdentifiers } from '../../library/selection/selectors';
import { RootState } from '../../store';
import { handleStandardSceneItemRightClick, leftClickItem } from '../../store/actions/sceneInteractionActions';
import { clearHoverItemIfActive, ensureHoverItemIsActive } from '../../store/actions/viewportActions';
import { getActiveAnalysisSet } from '../../store/selectors/getActiveAnalysisSetSelector';
import { getActiveColorByOption } from '../../store/selectors/getColorFromMemberDefinitionSelector';
import { getHighestCapacityForItem } from '../../store/selectors/getHighestCapacityForItemSelector';
import { getPlateAnalysis } from '../../store/selectors/livePlatingSelectors';
import { getPlateTypeFormatter } from '../../store/selectors/platePairFormatters';
import { getPlatePairViewStatus } from '../../store/selectors/viewStatusSelectors';
import { SelectedPlatePairDimensions } from '../dimension/SelectedPlatePairDimensions';
import { LabelVisual } from '../util/LabelVisual';
import {
  getPlatePairBoundsGeometry,
  getPlatePairEdgesGeometry,
  getPlatePairGeometry,
} from '../util/PlateGeometryCache';

const DEFAULT_COLOR = new Color(0xa0a0a0);
const PLATE_PAIR_EDGE_COLOR = 0x303030;
const PLATE_DIRECTION_INDICATOR_COLOR = 0xff0000;

const STAMPED_PLATE_TYPE_TEXT_COLOR = 0xc0c0c0;
const STAMPED_PLATE_TYPE_TEXT_SIZE = 0.25;

function plateAngleIsHorizontal(plateAngle: number) {
  return angleEquals(plateAngle, 0) || angleEquals(plateAngle, straightAngle);
}

interface Props {
  readonly platePair: PlatePair;
  readonly labels: React.JSX.Element | null;
}

export function PlatePairVisual({ platePair, labels }: Props) {
  const dispatch = useAppDispatch();
  const livePlateAnalysis = useAppSelector(getPlateAnalysis(platePair.guid));

  const plateDirectionIndicator1Ref = useRef<LineSegments | null>(null);
  const plateDirectionIndicator2Ref = useRef<LineSegments | null>(null);

  const plateDirectionIndicator1RefCallback = useCallback((newPlateDirectionIndicator1: LineSegments | null) => {
    if (plateDirectionIndicator1Ref.current === newPlateDirectionIndicator1) return;

    if (plateDirectionIndicator1Ref.current) {
      plateDirectionIndicator1Ref.current.geometry.dispose();
    }

    plateDirectionIndicator1Ref.current = newPlateDirectionIndicator1;
  }, []);

  const plateDirectionIndicator2RefCallback = useCallback((newPlateDirectionIndicator2: LineSegments | null) => {
    if (plateDirectionIndicator2Ref.current === newPlateDirectionIndicator2) return;

    if (plateDirectionIndicator2Ref.current) {
      plateDirectionIndicator2Ref.current.geometry.dispose();
    }

    plateDirectionIndicator2Ref.current = newPlateDirectionIndicator2;
  }, []);

  const viewStatus = useAppSelector(getPlatePairViewStatus)(platePair);
  const colorOverrideFromChecks = useAppSelector((state) => {
    if (getActiveColorByOption(state) === 'Combined') {
      if (!getActiveAnalysisSet(state)) return null;
      const capacity = getHighestCapacityForItem(state, platePair.guid, 'platePair');
      return capacity ? getColorFromCapacityRatio(capacity) : null;
    }

    return null;
  });
  const plateTypeFormatter = useAppSelector(getPlateTypeFormatter);
  const openContext = useAppSelector((state) => state.application.openContext?.type);
  const isInAssemblyContext = openContext === 'station' || openContext === 'schedule-station';
  const isPlatePairSelected = useAppSelector((state) => {
    const selectedItems = getSelectedItemIdentifiers(state);
    return selectedItems.some((item) => item.type === 'platePair' && item.guid === platePair.guid);
  });

  if (viewStatus === 'Hidden') return null;

  const matrix1 = getPlatePairPlate1Matrix(platePair);
  const matrix2 = getPlatePairPlate2Matrix(platePair);

  let plateDirectionIndicator: React.JSX.Element | null = null;
  if (
    isInAssemblyContext &&
    platePair.platePlacement === 'FrontBack' &&
    (!plateAngleIsHorizontal(platePair.angleX) ||
      !plateAngleIsHorizontal(platePair.angleY) ||
      !plateAngleIsHorizontal(platePair.angleZ))
  ) {
    const leftCenter = { x: 0, y: platePair.width / 2 };
    const rightCenter = { x: platePair.length, y: platePair.width / 2 };
    const frontPlatePairZ = 0.05;
    const backPlatePairZ = frontPlatePairZ * -1;

    const frontPlatePairLineStart = new Vector3(leftCenter.x, leftCenter.y, frontPlatePairZ);
    const frontPlatePairLineEnd = new Vector3(rightCenter.x, rightCenter.y, frontPlatePairZ);
    const backPlatePairLineStart = new Vector3(leftCenter.x, leftCenter.y, backPlatePairZ);
    const backPlatePairLineEnd = new Vector3(rightCenter.x, rightCenter.y, backPlatePairZ);
    const bufferGeometry1 = new BufferGeometry().setFromPoints([frontPlatePairLineStart, frontPlatePairLineEnd]);
    const bufferGeometry2 = new BufferGeometry().setFromPoints([backPlatePairLineStart, backPlatePairLineEnd]);

    plateDirectionIndicator = (
      <>
        <lineSegments
          ref={plateDirectionIndicator1RefCallback}
          matrixAutoUpdate={false}
          matrix={matrix1}
          geometry={bufferGeometry1}
        >
          <lineBasicMaterial
            color={PLATE_DIRECTION_INDICATOR_COLOR}
            opacity={viewStatus === 'Transparent' ? 0.25 : 1}
            transparent
          />
        </lineSegments>
        <lineSegments
          ref={plateDirectionIndicator2RefCallback}
          matrixAutoUpdate={false}
          matrix={matrix2}
          geometry={bufferGeometry2}
        >
          <lineBasicMaterial
            color={PLATE_DIRECTION_INDICATOR_COLOR}
            opacity={viewStatus === 'Transparent' ? 0.25 : 1}
            transparent
          />
        </lineSegments>
      </>
    );
  }

  const item = { type: 'platePair', guid: platePair.guid, origin: 'fiber' } as const;
  const eventHandlers: Partial<ThreeElements['mesh']> = {
    onClick: (event) => dispatch(leftClickItem(event, { item: platePair, type: 'platePair' })),
    onContextMenu: async (event) => await dispatch(handleStandardSceneItemRightClick(event, item)),
    onPointerOver: (event) => {
      event.stopPropagation();
      dispatch(ensureHoverItemIsActive(item));
    },
    onPointerOut: () => dispatch(clearHoverItemIfActive(item)),
  };

  const platePairVisuals = (
    <group>
      {livePlateAnalysis ? (
        // We use a key here to make sure that the material is not reused between the first child mesh of live plating
        // and the first child mesh of the real plate pair. Without the key, the plate would be transparent, since you
        // can't enable/disable transparency of a material after it's created.
        <Fragment key="live-plating">
          {/* Plate polygon to show portions of the plate not in contact with a member */}
          <mesh matrixAutoUpdate={false} matrix={matrix1} geometry={getPlatePairGeometry(platePair)}>
            <meshBasicMaterial color={DEFAULT_COLOR} />
          </mesh>

          <LivePlatingPolygons platePair={platePair} plateAnalysis={livePlateAnalysis} />
        </Fragment>
      ) : (
        <Fragment key="real-plate">
          <mesh matrixAutoUpdate={false} matrix={matrix1} geometry={getPlatePairGeometry(platePair)}>
            <meshBasicMaterial
              color={colorOverrideFromChecks ?? DEFAULT_COLOR}
              opacity={viewStatus === 'Transparent' ? 0.25 : 1}
              transparent
            />
          </mesh>
          <mesh matrixAutoUpdate={false} matrix={matrix2} geometry={getPlatePairGeometry(platePair)}>
            <meshBasicMaterial
              color={colorOverrideFromChecks ?? DEFAULT_COLOR}
              opacity={viewStatus === 'Transparent' ? 0.25 : 1}
              transparent
            />
          </mesh>
          <lineSegments matrixAutoUpdate={false} matrix={matrix1} geometry={getPlatePairEdgesGeometry(platePair)}>
            <lineBasicMaterial color={PLATE_PAIR_EDGE_COLOR} linewidth={0.5} />
          </lineSegments>
          <lineSegments matrixAutoUpdate={false} matrix={matrix2} geometry={getPlatePairEdgesGeometry(platePair)}>
            <lineBasicMaterial color={PLATE_PAIR_EDGE_COLOR} linewidth={0.5} />
          </lineSegments>
          {plateDirectionIndicator}
        </Fragment>
      )}
      {/* The userData-platePairGuid is used by raycasting. */}
      <mesh
        {...eventHandlers}
        matrixAutoUpdate={false}
        matrix={matrix1}
        geometry={getPlatePairBoundsGeometry(platePair)}
      >
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
      {/* The userData-platePairGuid is used by raycasting. */}
      <mesh
        {...eventHandlers}
        matrixAutoUpdate={false}
        matrix={matrix2}
        geometry={getPlatePairBoundsGeometry(platePair)}
      >
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
      {!livePlateAnalysis ? labels : null}

      <ErrorBoundary fallback={null}>
        <Suspense>
          <Text
            visible={!livePlateAnalysis}
            fontSize={STAMPED_PLATE_TYPE_TEXT_SIZE}
            renderOrder={RenderOrder.GlyphVisual}
            matrixAutoUpdate={false}
            matrix={getTextMatrix(platePair)}
          >
            {plateTypeFormatter(platePair.plateType, platePair.tangiblePlateTypesForComboPlateType)}
            <meshBasicMaterial color={STAMPED_PLATE_TYPE_TEXT_COLOR} depthTest={false} />
          </Text>
        </Suspense>
      </ErrorBoundary>

      {isPlatePairSelected ? <SelectedPlatePairDimensions platePair={platePair} /> : null}
    </group>
  );

  return platePair.platePlacement === 'TopBottom' ? <Hud renderPriority={2}>{platePairVisuals}</Hud> : platePairVisuals;
}

/**
 * The Y distance in inches between the center of the plate and where the label starts.
 */
const PLATE_LABEL_OFFSET_FROM_PLATE = 3;

/**
 * The distance between lines in the plate label, as a factor of text height.
 */
const PLATE_LABEL_LINE_SPACING = 0.1;

function getLengthHoleCount(length: number) {
  return Math.floor(length / plateSlotLength / 2);
}

function getTextMatrix(platePair: PlatePair) {
  const centeringShift = getLengthHoleCount(platePair.length) % 2 !== 0 ? plateSlotLength : 0;
  const zAxis = new Vector3(0, 0, 1);
  let localMatrix = new Matrix4()
    .makeTranslation(centeringShift, 0, 0)
    // rotate 90 degrees
    .multiply(new Matrix4().makeRotationAxis(zAxis, Math.PI / 2));
  const angleZ = convertDegreesToRadians(platePair.angleZ) % (Math.PI * 2);
  if ((angleZ > 0 && angleZ <= Math.PI) || angleZ <= -Math.PI) {
    localMatrix = localMatrix.premultiply(new Matrix4().makeRotationAxis(zAxis, Math.PI));
  }
  let x = platePair.length / 2;
  // The text on hinge plates is shifted so as not to be within the hinge hole
  if (platePair.plateType === 'MTH18') {
    x -= mth18HingeHoleRadius * 1.5;
  } else if (platePair.plateType === 'SMH18') {
    x -= smh18HingeHoleRadius * 1.5;
  }
  const y = platePair.width / 2;
  return localMatrix.premultiply(getPlatePairPlate1Matrix(platePair).multiply(new Matrix4().makeTranslation(x, y, 0)));
}

interface PlatePairLabelVisibility {
  showName: boolean;
  showType: boolean;
  showSize: boolean;
}

export const getPlatePairLabelVisibility = (
  shouldShowLabel: (guid: string, type: 'platePair') => boolean,
  platePairGuid: string,
  isMostCommonPlateType: boolean,
  isMostCommonPlateSize: boolean,
  visualOptions: RootState['visualOptions'],
): PlatePairLabelVisibility => {
  if (visualOptions.showAnalogModel || !visualOptions.showPlates || !shouldShowLabel(platePairGuid, 'platePair'))
    return { showName: false, showType: false, showSize: false };

  const { showPlateDetails, hideForMostCommonSize, hideForMostCommonType } = visualOptions.plateDetailSettings;
  return {
    showName: visualOptions.showPlateNames,
    showType: showPlateDetails && !(isMostCommonPlateType && hideForMostCommonType),
    showSize: showPlateDetails && !(isMostCommonPlateSize && hideForMostCommonSize),
  };
};

export interface PlatePairLabelPositions {
  readonly overallPlateLabelPosition: Point2D;

  // These values are null if their labels are hidden.
  readonly nameLabelPosition: Point2D | null;
  readonly typeLabelPosition: Point2D | null;
  readonly sizeLabelPosition: Point2D | null;
}

function getTotalPlateLabelsHeight(labelVisibility: PlatePairLabelVisibility, fontSize: number) {
  const { showName, showType, showSize } = labelVisibility;
  const numVisibleLabels = [showName, showType, showSize].filter((value) => value).length;
  if (numVisibleLabels === 0) return 0;

  return fontSize * (numVisibleLabels + PLATE_LABEL_LINE_SPACING * (numVisibleLabels - 1));
}

export function getInitialCenterPositionOfPlateLabels(
  platePair: PlatePair,
  labelVisibility: PlatePairLabelVisibility,
  fontSize: number,
  labelPositioningYThreshold: number,
) {
  const totalLabelHeight = getTotalPlateLabelsHeight(labelVisibility, fontSize);
  const labelOffsetDirection = platePair.centerY > labelPositioningYThreshold ? 1 : -1;
  const centerOfTotalLabel =
    platePair.centerY + labelOffsetDirection * (PLATE_LABEL_OFFSET_FROM_PLATE + totalLabelHeight / 2);
  return new Point2D(platePair.centerX, centerOfTotalLabel);
}

/**
 * Gets the plate labels' positions that is centered on a point. Returns `null` if all plate labels are hidden.
 */
export function getPlatePairLabelPositionsFromCenterPosition(
  labelVisibility: PlatePairLabelVisibility,
  fontSize: number,
  centerPosition: Point2D,
): PlatePairLabelPositions {
  const { showName, showType, showSize } = labelVisibility;

  const totalLabelHeight = getTotalPlateLabelsHeight(labelVisibility, fontSize);
  // Not correct if there is no first label, but all values would be `null` anyway in that case.
  const centerOfFirstLabel = centerPosition.y + totalLabelHeight / 2 - 0.5 * fontSize;
  const labelVerticalOffset = fontSize * (1 + PLATE_LABEL_LINE_SPACING);

  const nameLabelIndex = 0;
  const nameLabelPosition = showName
    ? new Point2D(centerPosition.x, centerOfFirstLabel - nameLabelIndex * labelVerticalOffset)
    : null;
  const typeLabelIndex = nameLabelIndex + (showName ? 1 : 0);
  const typeLabelPosition = showType
    ? new Point2D(centerPosition.x, centerOfFirstLabel - typeLabelIndex * labelVerticalOffset)
    : null;
  const sizeLabelIndex = typeLabelIndex + (showType ? 1 : 0);
  const sizeLabelPosition = showSize
    ? new Point2D(centerPosition.x, centerOfFirstLabel - sizeLabelIndex * labelVerticalOffset)
    : null;

  return {
    overallPlateLabelPosition: centerPosition,
    nameLabelPosition,
    typeLabelPosition,
    sizeLabelPosition,
  };
}

function formatPercentage(capacityRatio: number) {
  return `${Math.round(100 * capacityRatio)}%`;
}
function LivePlatingPolygons(props: { platePair: PlatePair; plateAnalysis: PlateAnalysis }) {
  const matrix = new Matrix4().makeTranslation(0, 0, props.platePair.distanceBetweenPlates);
  return (
    <>
      {props.plateAnalysis.memberAnalysis.map((grip) => {
        if (!grip.geometry) return null;

        const shape = new Shape(grip.geometry.map((point) => new Vector2(point.x, point.y)));

        const polygonGeometry = new ExtrudeGeometry(shape, {
          steps: 1,
          depth: getPlateThicknessForVisibility(props.platePair.platePlacement),
          bevelEnabled: false,
        });

        const center = new Polygon2D(grip.geometry.map((p) => new Point2D(p.x, p.y))).centroid;
        return (
          <React.Fragment key={grip.memberGuid}>
            <mesh matrixAutoUpdate={false} matrix={matrix} geometry={polygonGeometry}>
              <meshBasicMaterial color={getColorFromCapacityRatio(grip.capacityRatio)} opacity={0.8} transparent />
            </mesh>
            <LabelVisual
              renderOrder={RenderOrder.PlatingPercentageLabel}
              position={[center.x, center.y, 0]}
              fontSize={1}
              color={getColorFromCapacityRatio(grip.capacityRatio)}
            >
              {formatPercentage(grip.capacityRatio)}
            </LabelVisual>
          </React.Fragment>
        );
      })}
    </>
  );
}
